import React from "react";
import PropTypes from "prop-types";
import { InputText, Label } from "../../../../components/Inputs";
// import RichTextEditor from "../../../../components/RichTextEditor";
import { MinusIconButton } from "../../../../components/Buttons";
import { getContentTypeIdFromType, CONTENT_PANEL, getPanelContentData, isContentFromClipboard } from "../Utils";
import ConfirmationDialog from "./ConfirmationDialog";
import { Accordion, AccordionTab } from "../../../../components/Accordion";
import { DeleteIconButton, UpIconButton, DownIconButton, CopyIconButton } from "../../../../components/Buttons";
import {
  ContentMenuPanel, ContentMenuLabelMap,
  getContentTypeFromId, getContentForm,
  getContentDeleteConfirmationText
} from "../Utils";
import { copyToClipboard, getClipboardSize, isClipboardEmpty, pasteFromClipboard } from "../../ClipBoard";
import { notifyError, notifyInfo, notifySuccess } from "../../../../components/Utils";
import ClipBoardDialog from "../ClipBoardDialog";


const PanelForm = React.forwardRef(function PanelForm(props, ref) {
  const { className = "", onRemove, pageId, model = { title: "", body: "" }, onTitleChange } = props;

  const initialContents = React.useMemo(() => getPanelContentData(model), []);
  const [contents, setContents] = React.useState(initialContents);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const formRefs = React.useRef({});
  const titleFormRef = React.useRef(null);
  const dialogRef = React.useRef(null);
  const clipboardDialogRef = React.useRef(null);
  const isTabOpenList = React.useRef(initialContents.map(() => false));

  const getCurrentTabContent = (index) => {
    const idx = (index !== undefined && index !== null) ? index : activeIndex;
    if (idx !== null && formRefs.current[idx]) {
      return formRefs.current[idx].getModel(true);
    }
    return null;
  };

  const updateCurrentContent = () => {
    const oldContents = [...contents];
    if (activeIndex !== null && formRefs.current[activeIndex]) {
      const currentData = getCurrentTabContent();
      oldContents[activeIndex] = JSON.parse(JSON.stringify(currentData));
    }
    isTabOpenList.current.forEach((isOpen, index) => {
      if (isOpen) {
        const currentData = getCurrentTabContent(index);
        if (currentData) {
          oldContents[index] = JSON.parse(JSON.stringify(currentData));
        }
      }
    });
    return oldContents;
  };

  const validate = () => {
    let isValid = true;
    const titleModel = titleFormRef.current.getModel();
    if (!titleModel) {
      return null;
    }
    const newContents = [];
    contents.forEach((item, index) => {
      if (formRefs.current[index]) {
        let data = formRefs.current[index].getModel();
        if (!data) {
          isValid = false;
          data = item;
        }
        newContents.push({ ...data, position: (index + 1) });
      }
    });
    titleModel.body = JSON.stringify(newContents);
    if (!isValid) {
      return null;
    }
    return titleModel;
  };

  const getModel = (noValidate) => {
    const validatedModel = validate(noValidate);
    if (!validatedModel) {
      return null;
    }
    validatedModel.content_type_id = getContentTypeIdFromType(CONTENT_PANEL);
    const { id } = model;
    if (id) {
      validatedModel.id = id;
    }
    return validatedModel;
  };

  React.useImperativeHandle(ref, () => ({ getModel }));

  const changeTab = (newIndex) => {
    if (formRefs.current[activeIndex]) {
      const src = formRefs.current[activeIndex].getModel(true);
      const updated = contents.map((item, index) =>
        index === activeIndex ? { ...item, ...src } : item
      );
      setContents(updated);
      setActiveIndex(newIndex);
    }
  };

  const onOpenTabsChanged = (index, isOpen) => {
    isTabOpenList.current[index] = isOpen;
  };

  const removeContent = (currentIndex) => {
    const oldContents = updateCurrentContent();
    const updated = oldContents.filter((_, index) => index !== currentIndex);
    setContents(updated);
  };

  const confirmContentRemoval = (type, currentIndex) => {
    const confirmData = getContentDeleteConfirmationText(type);
    if (confirmData) {
      dialogRef.current.show(
        true,
        confirmData,
        () => removeContent(currentIndex)
      );
    }
  };

  const addContent = (content_type_id, position, type) => {
    console.log("PanelForm.addContent()", content_type_id, position);
    const oldContents = updateCurrentContent();
    const newItemIndex = position ? (position - 1) : oldContents.length - 1;
    const handleAddition = (items) => {
      oldContents.splice(position - 1, 0, ...items);
      setContents([...oldContents]);
      setActiveIndex(newItemIndex);
    };
    if (isContentFromClipboard(type)) {
      if (isClipboardEmpty()) {
        return notifyInfo({
          summary: "Zwischenablage is leer",
          detail: "Es kann nichts hinzugefügt werden!"
        });
      } else if (getClipboardSize() === 1) {
        return handleAddition([ pasteFromClipboard() ]);
      }
      clipboardDialogRef.current.show(true, handleAddition);
    } else {
      handleAddition([
        {
          content_type_id,
          title: "",
          subtitle: "",
          body: ""
        }
      ]);
    }
  };

  const move = (currentIndex, up) => {
    const targetIndex = up ? currentIndex - 1 : currentIndex + 1;
    const oldContents = updateCurrentContent();
    const src = formRefs.current[currentIndex].getModel(true);
    let tgt = formRefs.current[targetIndex].getModel(true);
    tgt = { ...oldContents[targetIndex], ...tgt };
    const updated = [];
    for (let index = 0; index < oldContents.length; index++) {
      if (index === currentIndex) {
        updated.push(tgt);
      } else if (index === targetIndex) {
        updated.push(src);
      } else {
        updated.push({ ...oldContents[index] });
      }
    }
    setContents(updated);
    setActiveIndex(targetIndex);
  };

  const copy = (index) => {
    const data = formRefs.current[index].getModel(true);
    const num = copyToClipboard(data);
    notifySuccess({
      summary: "Zwischenablage",
      detail: `Der Inhalt wurde in die Zwischenablage hinzugefügt. Sie enthält nun ${num} Element${num > 0 ? "e" : ""}`
    });
  };

  const prefix = Math.random();
  const panelId = `${pageId}_` + `${prefix}`.split(".").join("-");
  const createRefMaker = index => r => { formRefs.current[index] = r; };
  const bodyMarkup = contents
    .filter(item => item !== null)
    .map((item, index) => {
      const { content_type_id, ...itemModel } = item;
      const type = getContentTypeFromId(content_type_id);
      const Component = getContentForm(type);
      if (!Component) {
        console.error("<< Content Type Error: unknown content type", type, item);
        return null;
      }
      const menu = (
        <div className="display-inline-block- clearfix">
          <UpIconButton
            className="w3-border margin-rht pad-sm"
            onClick={() => move(index, true)}
            disabled={!(contents.length > 1 && index > 0)}
          />
          <DownIconButton
            className="w3-border margin-rht pad-sm"
            onClick={() => move(index, false)}
            disabled={!(contents.length > 1 && index < contents.length - 1)}
          />
          <CopyIconButton
            className="w3-border margin-rht pad-sm"
            onClick={() => copy(index)}
            title="in die Zwischenablage kopieren"
          />
          <DeleteIconButton
            className="w3-border pad-sm"
            onClick={() => confirmContentRemoval(type, index)}
          />
        </div>
      );
      const counter = `${index < 9 ? "0" : ""}${index + 1}. `;
      return (
        <AccordionTab key={`${prefix}-${index}`} header={counter + ContentMenuLabelMap[type]} menu={menu} className="margin-top">
          <Component
            ref={createRefMaker(index)}
            className=""
            model={itemModel}
            pageId={panelId}
          />
        </AccordionTab>
      );
    });

  const menuMarkup = onRemove && (
    <div className="w3-right">
      <div className="w3-border"><MinusIconButton onClick={onRemove}/></div>
    </div>
  );
  return (
    <div id={panelId} className={`${className} clearfix pad-big`}>
      {menuMarkup}
      <PanelContentTitleForm
        ref={titleFormRef}
        model={model}
        onTitleChange={onTitleChange}
      />
      <div className="w3-border-top w3-border-bottom w3-light-grey neg-margin-big-lr margin-big-top pad">
        <ContentMenuPanel
          className="w3-right"
          onAddContent={addContent}
          positions={contents.length + 1}
        />
        <h4 className="no-margin pad-sm-tb">{"Abschnittsinhalte"}</h4>
      </div>

      <div className="clearfix pad-btm" style={{paddingTop: "10px"}}>
        <Accordion
          id="pageContents"
          className="clearfix"
          activeIndex={activeIndex}
          onTabChange={(event) => changeTab(event.index)}
          notDraggable
          singleActiveIndex={false}
        >
          {bodyMarkup}
        </Accordion>
        <ConfirmationDialog ref={dialogRef}/>
        <ClipBoardDialog ref={clipboardDialogRef}/>
      </div>
      
  </div>
  );
});

PanelForm.propTypes = {
  pageId: PropTypes.string,
  className: PropTypes.string,
  model: PropTypes.object,
  onRemove: PropTypes.func,
};

PanelForm.defaultProps = {
  className: "",
  model: {
    title: "",
    body: ""
  },
};

export default PanelForm;


const PanelContentTitleForm = React.forwardRef(function PanelContentTitleForm(props, ref) {
  const { className = "", onTitleChange } = props;
  const initialModel = props.model || {};

  const [title, setTitle] = React.useState(initialModel.title || "");
  const [error, setError] = React.useState({ title: null });

  const validate = (noValidate) => {
    let isValid = true;
    const newError = { ...error };
    if (!title || title === "") {
      newError.title = true;
      isValid = false;
    }
    const model = { title };
    if (!noValidate && !isValid) {
      notifyError({
        summary: `Abschnitt mit Titel`,
        detail: `Sie müssen einen Abschnittstitel angeben!`
      });
      setError(newError);
      return null;
    }
    return model;
  };

  const getModel = (noValidate) => {
    return validate(noValidate);
  };

  React.useImperativeHandle(ref, () => ({ getModel }));

  const onChange = (event) => {
    setTitle(event.target.value);
    if (onTitleChange) {
      onTitleChange(event.target.value);
    }
  };

  return (
    <div className={`${className}`}>
      <div className={`form-group-item ${error.title ? "error-group" : ""}`}>
        <Label htmlFor="title">{`Titel`}<span>*</span></Label>
        <InputText
          id="title"
          className="w3-block w3-border"
          value={title}
          onChange={onChange}
        />
        {error.title && (<span className="w3-block w3-red">{error.title}</span>)}
      </div>
    </div>
  );
});

PanelContentTitleForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.object,
  onTitleChange: PropTypes.func,
};

PanelContentTitleForm.defaultProps = {
  className: "",
  model: { title: "" }
};

